import React from 'react';
import { Bell, Settings as SettingsIcon, LogOut, Heart, Clock, Music, TrendingUp, Calendar, X } from 'lucide-react';

const getActivityIcon = (type) => {
  switch(type) {
    case 'song_created': return <Music className="w-4 h-4 text-brand" />;
    case 'version_uploaded': return <TrendingUp className="w-4 h-4 text-green-400" />;
    case 'gig_created': return <Calendar className="w-4 h-4 text-blue-400" />;
    default: return <Clock className="w-4 h-4 text-text-subtle" />;
  }
};

const getActivityLabel = (type) => {
  switch(type) {
    case 'song_created': return 'Created song';
    case 'version_uploaded': return 'Uploaded version';
    case 'gig_created': return 'Added gig';
    default: return 'Activity';
  }
};

const formatTimeAgo = (timestamp) => {
  if (!timestamp) return '';
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return then.toLocaleDateString();
};

const handleShareTapsody = async () => {
  const shareText = `Hey! Wij gebruiken een app om onze bandchaos aan te pakken, zoals verloren song ideeën, onduidelijkheid over gigs, dat soort dingen. Er zit ook een database in met 1.750+ zalen en festivals in Nederland. Misschien handig voor jullie? Check: https://tapsody.app`;

  if (navigator.share) {
    try {
      await navigator.share({
        title: 'Tapsody',
        text: shareText,
        url: 'https://tapsody.app'
      });
    } catch (err) {
      // User cancelled or share failed — ignore
      if (err.name !== 'AbortError') {
        console.error('Share failed:', err);
      }
    }
  } else {
    // Desktop fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(shareText);
      alert('Link gekopieerd! Plak het in een bericht naar een bevriende band.');
    } catch (err) {
      console.error('Copy failed:', err);
    }
  }
};

export default function AppHeader({ 
  activeTab, 
  setActiveTab, 
  showNotifications, 
  setShowNotifications, 
  notificationRef, 
  unreadComments, 
  recentActivity, 
  onLogout 
}) {
  return (
    <div className="mb-6 sm:mb-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={`${process.env.PUBLIC_URL}/tapsody-logo.png`} alt="Tapsody Logo" className="h-12 sm:h-16 w-auto" />
        </div>
        <div className="flex items-center gap-2">
          {/* Recommend Tapsody */}
          <button
            onClick={handleShareTapsody}
            className="bg-transparent hover:bg-brand transition p-2 rounded-full"
            title="Love the app? Recommend Tapsody!"
          >
            <Heart className="w-5 h-5 text-white fill-brand" />
          </button>

          {/* Notifications Bell */}
          <div className="relative" ref={notificationRef}>
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="bg-transparent hover:bg-brand transition p-2 rounded-full relative"
              title="Notifications"
            >
              <Bell className="w-5 h-5 text-white" />
              {unreadComments > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-bold">
                  {unreadComments > 9 ? '9+' : unreadComments}
                </span>
              )}
            </button>

            {/* Notification Popup */}
            {showNotifications && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                {/* Blurred backdrop */}
                <div 
                  className="absolute inset-0 bg-black/60 backdrop-blur-md" 
                  onClick={() => setShowNotifications(false)} 
                />

                {/* Floating card */}
                <div className="relative w-full max-w-md card-3d rounded-card p-6 sm:p-8 text-white max-h-[80vh] flex flex-col">
                  {/* Close Button */}
                  <button
                    onClick={() => setShowNotifications(false)}
                    className="absolute top-4 right-4 bg-transparent hover:bg-brand transition p-2 rounded-full"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>

                  {/* Header */}
                  <div className="mb-4 flex items-center gap-3">
                    <Bell className="w-5 h-5 text-brand" />
                    <h3 className="font-semibold text-lg">Activity</h3>
                    {unreadComments > 0 && (
                      <span className="text-xs bg-brand/20 text-brand px-2 py-0.5 rounded-full">
                        {unreadComments} unread
                      </span>
                    )}
                  </div>

                  {/* Scrollable content */}
                  <div className="flex-1 overflow-y-auto -mx-6 px-6 sm:-mx-8 sm:px-8">
                    {recentActivity.length === 0 ? (
                      <div className="py-10 text-center text-text-subtle">
                        <Clock className="w-10 h-10 mx-auto mb-3 opacity-50" />
                        <p className="text-sm">No recent activity</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-surface-border-light">
                        {recentActivity.map((activity, idx) => (
                          <div key={idx} className="flex items-start gap-3 p-3 hover:bg-white/5 transition">
                            <div className="mt-0.5">
                              {getActivityIcon(activity.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{activity.name}</p>
                              <p className="text-xs text-text-subtle">
                                {getActivityLabel(activity.type)} by {activity.user}
                              </p>
                            </div>
                            <span className="text-xs text-text-subtle whitespace-nowrap">
                              {formatTimeAgo(activity.timestamp)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Settings */}
          <button
            onClick={() => { setActiveTab('settings'); setShowNotifications(false); }}
            className={`bg-transparent hover:bg-brand transition p-2 rounded-full ${activeTab === 'settings' ? 'bg-brand' : ''}`}
            title="Settings"
          >
            <SettingsIcon className="w-5 h-5 text-white" />
          </button>

          {/* Log out */}
          <button
            onClick={onLogout}
            className="bg-transparent hover:bg-brand transition p-2 rounded-full"
            title="Log out"
          >
            <LogOut className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>
    </div>
  );
}
